import { useSyncExternalStore, type ReactNode } from 'react'

export type NoticeKind = 'success' | 'error' | 'info'
/** a short-lived chip in the activity bar: a result or a refusal, with its own mark (a token's logo) or the kind's */
export type Notice = { id: number; kind: NoticeKind; title: ReactNode; body?: ReactNode; icon?: ReactNode; ms: number }

/**
 * The notices the activity bar shows among the flows (John, Nov 5 2026:
 * "I don't want any of the floating snack bars. Every snack bar should just
 * be converted into a progress item"). One store for the page: anything may
 * push, the bar draws, each notice goes by itself after its time or on a
 * click.
 */
let notices: Notice[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => {
  for (const listener of listeners) listener()
}

export function pushNotice(input: Omit<Notice, 'id'>): number {
  const id = nextId++
  // the newest few only: a burst never fills the bar
  notices = [...notices.slice(-4), { ...input, id }]
  emit()
  window.setTimeout(() => dismissNotice(id), input.ms)
  return id
}

export function dismissNotice(id: number) {
  if (!notices.some((notice) => notice.id === id)) return
  notices = notices.filter((notice) => notice.id !== id)
  emit()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
const NONE: Notice[] = []
export const useNotices = () => useSyncExternalStore(subscribe, () => notices, () => NONE)
